import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { useMultiAttachmentFileChangeHook, useDownloadButtonClicked } from '../../../HelperMethods/CommonHooks';
import { getFileName } from '../../../HelperMethods/UtilityFunctions';

function FileComponent({ uploadedFile, onCancelAnAttachment, disabled }) {
  const onDownloadButtonClicked = useDownloadButtonClicked(uploadedFile);
  return (
    <div className="grid-item">
      <div className="attachment-label">
        <span>{uploadedFile.fileName || getFileName(uploadedFile.key)}</span>
      </div>
      <div className="flex-inside">
        <Button type="button" icon="cloud download" onClick={onDownloadButtonClicked} />
        <Button
          disabled={disabled}
          type="button"
          icon="cancel"
          onClick={() => {
            onCancelAnAttachment(uploadedFile.key);
          }}
        />
      </div>
    </div>
  );
}

export default function MultiAttachmentComponent({
  fieldName,
  fileSizeLimitInMB,
  // allowedFormats,
  uploadedFiles = [],
  onChange,
  disabled,
  errorMessage,
  onUploadStatusChange,
  isRequired = false,
  onCancelAnAttachment,
  maxLimitNumberOfFiles,
  print = false,
  isCreate = false,
  isRequiredOnCreation = false,
}) {
  disabled = disabled || uploadedFiles?.length >= maxLimitNumberOfFiles;
  const fileInputRef = useRef();
  const [errorMsg, setErrorMsg] = useState(null);
  const loggedInUser = useSelector((state) => state.appReducer.loggedInUser);
  // const acceptAttributes = getAcceptAttribute(allowedFormats);
  const [uploading, onFileChange] = useMultiAttachmentFileChangeHook({
    onAttachmentUpload: onChange,
    fileSizeLimitInMB,
    handleError: setErrorMsg,
    loggedInUser,
    maxLimitNumberOfFiles,
    uploadedFilesLength: uploadedFiles?.length ?? 0,
  });
  React.useEffect(() => {
    if (onUploadStatusChange) onUploadStatusChange(uploading);
  }, [uploading]);
  const onUploadButtonClicked = useCallback(() => {
    fileInputRef.current.click();
  }, [fileInputRef]);

  return (
    <div className="multi-attachment-container">
      <label>
        {fieldName}

        {!isCreate && isRequired && <span className="mandatory-resolve-field">*</span>}
        {isCreate ? (
          isRequiredOnCreation ? (
            <span className="mandatory-field">*</span>
          ) : isRequired ? (
            <span className="mandatory-resolve-field">*</span>
          ) : (
            ''
          )
        ) : (
          ''
        )}
      </label>
      <div className="no-print">
        <div className="grid-container">
          {uploadedFiles.map((uploadedFile) => (
            <FileComponent onCancelAnAttachment={onCancelAnAttachment} uploadedFile={uploadedFile} />
          ))}
        </div>
        <>
          <div style={{ marginLeft: '15px' }}>
            (Max {maxLimitNumberOfFiles} Files can be uploaded & Max 10 Files can be uploaded at a time)
          </div>

          <Button
            type="button"
            style={{ margin: '15px 0 15px 20px' }}
            loading={uploading}
            disabled={uploading || disabled}
            icon="cloud upload"
            onClick={onUploadButtonClicked}
            className="no-print"
          />
          <input
            style={{ display: 'none' }}
            ref={fileInputRef}
            // accept={acceptAttributes}
            disabled={disabled}
            id={fieldName}
            type="file"
            onChange={onFileChange}
            multiple
          />
          <span style={{ marginLeft: '5px' }}>Upload </span>
        </>
        {(errorMessage || errorMsg) && <div className="errorText">* {errorMessage || errorMsg}</div>}
      </div>
      {print && <span>: {uploadedFiles?.length ?? 0} Attachments</span>}
    </div>
  );
}

MultiAttachmentComponent.propTypes = {
  fieldName: PropTypes.string.isRequired,
  fileSizeLimitInMB: PropTypes.number.isRequired,
  // allowedFormats: PropTypes.arrayOf(PropTypes.string).isRequired,
  uploadedFiles: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      bucket: PropTypes.string,
      region: PropTypes.string,
      fileName: PropTypes.string,
    })
  ),
  onChange: PropTypes.func.isRequired,
  onCancelAnAttachment: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  onUploadStatusChange: PropTypes.func,
  isRequired: PropTypes.bool,
  maxLimitNumberOfFiles: PropTypes.number,
  print: PropTypes.bool.isRequired,
};

MultiAttachmentComponent.defaultProps = {
  uploadedFiles: [],
  disabled: false,
  errorMessage: null,
  maxLimitNumberOfFiles: 20,
};
